/**
 * ============================================================================
 * ClinIA Perú - Catálogo CIE-10 para búsqueda
 * ============================================================================
 * Subconjunto de la CIE-10 orientado a la consulta ambulatoria, con peso en
 * gastroenterología y salud mental, que es lo que atiende hoy el sistema.
 *
 * Por qué un catálogo en el frontend y no una tabla:
 *   Son unos cientos de códigos y no cambian. Tenerlos aquí hace la búsqueda
 *   instantánea, sin viaje al servidor, que es lo que importa cuando el médico
 *   está tecleando con el paciente delante. Si algún día hace falta la CIE-10
 *   completa (14.000 códigos), entonces sí toca moverla a la base de datos.
 *
 * `alias` recoge cómo lo dice la gente de verdad ("gastritis" para K29.7,
 * "colon irritable" para K58.9), porque nadie busca por el nombre oficial.
 * ============================================================================
 */

export const CIE10_CATALOG = [
  // --- Digestivo: esófago, estómago, duodeno ---
  { code: 'K21.0', name: 'Enfermedad por reflujo gastroesofágico con esofagitis', alias: 'reflujo erge esofagitis' },
  { code: 'K21.9', name: 'Enfermedad por reflujo gastroesofágico sin esofagitis', alias: 'reflujo erge' },
  { code: 'K25.9', name: 'Úlcera gástrica', alias: 'ulcera estomago' },
  { code: 'K26.9', name: 'Úlcera duodenal', alias: 'ulcera duodeno' },
  { code: 'K29.0', name: 'Gastritis aguda hemorrágica', alias: 'gastritis sangrado' },
  { code: 'K29.5', name: 'Gastritis crónica', alias: 'gastritis cronica' },
  { code: 'K29.7', name: 'Gastritis no especificada', alias: 'gastritis' },
  { code: 'K29.8', name: 'Duodenitis', alias: 'duodenitis' },
  { code: 'K30', name: 'Dispepsia', alias: 'dispepsia indigestion llenura' },
  { code: 'K31.9', name: 'Enfermedad del estómago y del duodeno, no especificada', alias: 'estomago' },

  // --- Digestivo: intestino ---
  { code: 'K52.9', name: 'Gastroenteritis y colitis no infecciosa', alias: 'gastroenteritis colitis diarrea' },
  { code: 'K57.9', name: 'Enfermedad diverticular del intestino', alias: 'diverticulos diverticulitis' },
  { code: 'K58.0', name: 'Síndrome del intestino irritable con diarrea', alias: 'colon irritable sii diarrea' },
  { code: 'K58.9', name: 'Síndrome del intestino irritable sin diarrea', alias: 'colon irritable sii' },
  { code: 'K59.0', name: 'Estreñimiento', alias: 'constipacion estreñimiento estrenimiento' },
  { code: 'K59.1', name: 'Diarrea funcional', alias: 'diarrea' },
  { code: 'K60.2', name: 'Fisura anal', alias: 'fisura anal' },
  { code: 'K64.9', name: 'Hemorroides', alias: 'hemorroides almorranas' },
  { code: 'K62.5', name: 'Hemorragia del ano y del recto', alias: 'sangrado rectal rectorragia' },
  { code: 'K92.2', name: 'Hemorragia gastrointestinal', alias: 'hemorragia digestiva sangrado' },
  { code: 'K92.0', name: 'Hematemesis', alias: 'vomito con sangre' },
  { code: 'K92.1', name: 'Melena', alias: 'melena heces negras' },

  // --- Hígado, vesícula y páncreas ---
  { code: 'K70.3', name: 'Cirrosis hepática alcohólica', alias: 'cirrosis alcohol' },
  { code: 'K74.6', name: 'Cirrosis hepática', alias: 'cirrosis' },
  { code: 'K76.0', name: 'Hígado graso (esteatosis hepática)', alias: 'higado graso esteatosis' },
  { code: 'K80.2', name: 'Cálculo de la vesícula biliar (colelitiasis)', alias: 'calculos vesicula piedras colelitiasis' },
  { code: 'K81.0', name: 'Colecistitis aguda', alias: 'colecistitis' },
  { code: 'K85.9', name: 'Pancreatitis aguda', alias: 'pancreatitis' },
  { code: 'B18.2', name: 'Hepatitis viral tipo C crónica', alias: 'hepatitis c' },
  { code: 'B18.1', name: 'Hepatitis viral tipo B crónica', alias: 'hepatitis b' },

  // --- Infecciones frecuentes ---
  { code: 'A09', name: 'Diarrea y gastroenteritis de presunto origen infeccioso', alias: 'gastroenteritis infecciosa' },
  { code: 'B98.0', name: 'Helicobacter pylori como causa de enfermedades', alias: 'h pylori helicobacter' },
  { code: 'B82.9', name: 'Parasitosis intestinal', alias: 'parasitos parasitosis' },
  { code: 'N39.0', name: 'Infección de vías urinarias', alias: 'itu infeccion urinaria' },
  { code: 'J00', name: 'Rinofaringitis aguda (resfriado común)', alias: 'resfrio gripe' },
  { code: 'J02.9', name: 'Faringitis aguda', alias: 'faringitis dolor de garganta' },
  { code: 'J20.9', name: 'Bronquitis aguda', alias: 'bronquitis' },

  // --- Síntomas y signos ---
  { code: 'R10.1', name: 'Dolor abdominal localizado en parte superior', alias: 'dolor epigastrio abdominal superior' },
  { code: 'R10.4', name: 'Dolor abdominal no especificado', alias: 'dolor abdominal barriga' },
  { code: 'R11', name: 'Náusea y vómito', alias: 'nausea vomito' },
  { code: 'R12', name: 'Pirosis (acidez)', alias: 'acidez ardor pirosis' },
  { code: 'R14', name: 'Flatulencia y distensión abdominal', alias: 'gases distension hinchazon' },
  { code: 'R16.0', name: 'Hepatomegalia', alias: 'higado grande' },
  { code: 'R17', name: 'Ictericia', alias: 'ictericia amarillo' },
  { code: 'R51', name: 'Cefalea', alias: 'dolor de cabeza cefalea' },
  { code: 'R53', name: 'Malestar y fatiga', alias: 'cansancio fatiga debilidad' },
  { code: 'R63.4', name: 'Pérdida de peso anormal', alias: 'baja de peso adelgazamiento' },

  // --- Crónicas y metabólicas ---
  { code: 'E11.9', name: 'Diabetes mellitus tipo 2', alias: 'diabetes dm2' },
  { code: 'E11.5', name: 'Diabetes mellitus tipo 2 con complicaciones circulatorias', alias: 'pie diabetico diabetes' },
  { code: 'E66.9', name: 'Obesidad', alias: 'obesidad sobrepeso' },
  { code: 'E78.5', name: 'Hiperlipidemia', alias: 'colesterol trigliceridos' },
  { code: 'I10', name: 'Hipertensión esencial (primaria)', alias: 'hipertension presion alta hta' },
  { code: 'D50.9', name: 'Anemia por deficiencia de hierro', alias: 'anemia ferropenica' },
  { code: 'E03.9', name: 'Hipotiroidismo', alias: 'tiroides hipotiroidismo' },

  // --- Salud mental ---
  { code: 'F32.0', name: 'Episodio depresivo leve', alias: 'depresion leve' },
  { code: 'F32.1', name: 'Episodio depresivo moderado', alias: 'depresion moderada' },
  { code: 'F32.2', name: 'Episodio depresivo grave sin síntomas psicóticos', alias: 'depresion grave' },
  { code: 'F32.9', name: 'Episodio depresivo no especificado', alias: 'depresion' },
  { code: 'F33.9', name: 'Trastorno depresivo recurrente', alias: 'depresion recurrente' },
  { code: 'F41.0', name: 'Trastorno de pánico', alias: 'panico crisis de ansiedad' },
  { code: 'F41.1', name: 'Trastorno de ansiedad generalizada', alias: 'ansiedad generalizada tag' },
  { code: 'F41.2', name: 'Trastorno mixto de ansiedad y depresión', alias: 'ansiedad y depresion' },
  { code: 'F41.9', name: 'Trastorno de ansiedad no especificado', alias: 'ansiedad' },
  { code: 'F43.0', name: 'Reacción al estrés agudo', alias: 'estres agudo' },
  { code: 'F43.1', name: 'Trastorno de estrés postraumático', alias: 'tept trauma estres postraumatico' },
  { code: 'F43.2', name: 'Trastorno de adaptación', alias: 'adaptacion duelo' },
  { code: 'F45.3', name: 'Disfunción autonómica somatomorfa', alias: 'somatizacion' },
  { code: 'F48.0', name: 'Neurastenia', alias: 'agotamiento neurastenia' },
  { code: 'F51.0', name: 'Insomnio no orgánico', alias: 'insomnio no duermo' },
  { code: 'F90.0', name: 'Trastorno de la actividad y de la atención (TDAH)', alias: 'tdah atencion hiperactividad' },
  { code: 'F10.2', name: 'Síndrome de dependencia del alcohol', alias: 'alcoholismo dependencia alcohol' },

  // --- Otros frecuentes ---
  { code: 'M54.5', name: 'Lumbago', alias: 'dolor lumbar espalda lumbago' },
  { code: 'M79.7', name: 'Fibromialgia', alias: 'fibromialgia' },
  { code: 'L30.9', name: 'Dermatitis no especificada', alias: 'dermatitis alergia piel' },
  { code: 'Z00.0', name: 'Examen médico general', alias: 'chequeo control examen' },
  { code: 'Z71.3', name: 'Consulta para instrucción dietética', alias: 'dieta nutricion consejo' },
];

/**
 * Quita tildes y pasa a minúsculas, para que "cefalea" encuentre "Cefalea" y
 * "estreñimiento" encuentre "estrenimiento".
 */
const normalize = (text) =>
  (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Busca en el catálogo por código, nombre o alias.
 *
 * Ordena por relevancia: primero lo que empieza por el término (quien escribe
 * "K30" quiere K30 el primero), después lo que lo contiene.
 *
 * @param {string} query
 * @param {number} [limit=8]
 * @returns {Array<{code, name, alias}>}
 */
export const searchCie10 = (query, limit = 8) => {
  const term = normalize(query).trim();
  if (term.length < 2) return [];

  const scored = [];
  for (const entry of CIE10_CATALOG) {
    const code = normalize(entry.code);
    const name = normalize(entry.name);
    const alias = normalize(entry.alias);

    let score = null;
    if (code === term) score = 0;
    else if (code.startsWith(term)) score = 1;
    else if (name.startsWith(term)) score = 2;
    else if (alias.split(' ').some(w => w.startsWith(term))) score = 3;
    else if (name.includes(term) || alias.includes(term)) score = 4;

    if (score !== null) scored.push({ entry, score });
  }

  return scored
    .sort((a, b) => a.score - b.score || a.entry.code.localeCompare(b.entry.code))
    .slice(0, limit)
    .map(s => s.entry);
};

/** Nombre oficial de un código, o cadena vacía si no está en el catálogo */
export const describeCie10 = (code) => {
  const found = CIE10_CATALOG.find(e => e.code.toLowerCase() === (code || '').toLowerCase().trim());
  return found ? found.name : '';
};
